import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, Table, TableHead,
  TableBody, TableRow, TableCell, CircularProgress, Divider
} from '@mui/material';
import { ArrowBack, Print } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym, locale } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useSuppliers } from '../hooks/useSuppliers';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtD = (d) => d ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PurchaseOrderPrint = () => {
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const { suppliers } = useSuppliers();

  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    axios.get(`${API}/api/payables/orders/${orderNo}`, { headers: hdrs() })
      .then(r => setOrder(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [orderNo]);

  if (loading) return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>
  );
  if (error || !order) return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>
  );

  const supplier = suppliers.find(s => s.supplierCode === order.supplierCode);
  const subtotal = order.subtotal ?? (order.lines || []).reduce((s, l) => s + (l.amount || 0), 0);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .po-card { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <Box display="flex" gap={1.5} mb={2} className="no-print">
        <Button startIcon={<ArrowBack />} variant="outlined" size="small"
          onClick={() => navigate('/payables/orders')}>
          Back
        </Button>
        <Button startIcon={<Print />} variant="contained" size="small"
          onClick={() => window.print()}
          sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
          Print / Save PDF
        </Button>
      </Box>

      <Card elevation={3} className="po-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="PURCHASE ORDER" subtitle={order.orderNo} />

          {/* Vendor + document meta */}
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">SUPPLIER</Typography>
              <Typography variant="body1" fontWeight={700}>
                {supplier?.supplierName || order.supplierCode}
              </Typography>
              {supplier?.address && <Typography variant="body2">{supplier.address}</Typography>}
              {supplier?.taxNo && <Typography variant="body2" color="text.secondary">Revenue Authority No.: {supplier.taxNo}</Typography>}
              {supplier?.email && <Typography variant="body2" color="text.secondary">{supplier.email}</Typography>}
            </Box>
            <Box textAlign="right">
              <Typography variant="body2"><strong>Order No:</strong> {order.orderNo}</Typography>
              <Typography variant="body2"><strong>Order Date:</strong> {fmtD(order.orderDate)}</Typography>
              <Typography variant="body2"><strong>Expected:</strong> {fmtD(order.expectedDate)}</Typography>
              {order.locationCode && <Typography variant="body2"><strong>Deliver to:</strong> {order.locationCode}</Typography>}
              <Typography variant="body2"><strong>Status:</strong> {order.status}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Item / GL</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>VAT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(order.lines || []).map((l, idx) => (
                <TableRow key={idx}>
                  <TableCell>{l.lineNo || idx + 1}</TableCell>
                  <TableCell>{l.lineType}</TableCell>
                  <TableCell>{l.itemNo || l.faNo || l.glAccount || '—'}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{fmt(l.unitPrice)}</TableCell>
                  <TableCell align="right" title={l.vatPercent ? `${l.vatPercent}%` : ''}>
                    {l.vatAmount ? fmt(l.vatAmount) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(l.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3} mt={2}>
            <Typography variant="body2" color="text.secondary">Subtotal: {sym()} {fmt(subtotal)}</Typography>
            <Typography variant="body2" color="text.secondary">VAT: {sym()} {fmt(order.totalVAT || 0)}</Typography>
            <Typography variant="h6" fontWeight={700}>Total: {sym()} {fmt(order.totalAmount || 0)}</Typography>
          </Box>

          <Box mt={4} display="flex" justifyContent="space-between" flexWrap="wrap" gap={4}>
            <Box>
              <Divider sx={{ width: 200, mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary">Prepared by</Typography>
            </Box>
            <Box>
              <Divider sx={{ width: 200, mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary">Authorised by</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseOrderPrint;
